<!--
  Right panel, ported from vendor-ref/mara/app/ui/src/components/nanome/WorkspacePanelRight.vue (Tabs: Entries,
  Ligands, MARA, Import, Users; w-lg expanded, w-12 rail collapsed, w-full in the mobile sheet) with the header row
  of WorkspaceInfo.vue (collapse chevrons, name, XR button). The Comments tab is collab-only and not ported.
  Like prod, an empty scene opens on Import and an import switches back to Entries.
-->
<script setup lang="ts">
import NanomeIcon from '@/components/NanomeIcon.vue';
import { computed, provide, ref, watch } from 'vue';
import Button from 'primevue/button';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import { useLiteStore } from '@nanome-lite/state';
import { useDevice } from '@/composables/device';
import WorkspaceEntries from './WorkspaceEntries.vue';
import WorkspaceLigands from './ligands/WorkspaceLigands.vue';
import WorkspaceChat from './chat/WorkspaceChat.vue';
import WorkspaceImportFile from './WorkspaceImportFile.vue';
import WorkspaceMembers from './WorkspaceMembers.vue';
import { OPEN_IN_NANOME } from './open-in-nanome-key';

export type RightTab = 'entries' | 'ligands' | 'mara' | 'import' | 'users';

const props = defineProps<{ collapsed: boolean }>();
const emit = defineEmits<{ 'update:collapsed': [v: boolean]; 'open-in-nanome': [] }>();
const rightTab = defineModel<RightTab>('tab', { default: 'entries' });

const store = useLiteStore();
const device = useDevice();
const isMobile = computed(() => device.isMobile.value);
const isSidebar = computed(() => props.collapsed && !isMobile.value);
const title = computed(() => {
  const [first, ...rest] = store.entries;
  if (!first) return 'Nanome Lite';
  return rest.length ? `${first.name} +${rest.length}` : first.name;
});

provide(OPEN_IN_NANOME, () => emit('open-in-nanome'));

// an empty scene is most useful on the Import tab; the first entry in brings Entries back
const hadEntries = ref(store.entries.length > 0);
if (!hadEntries.value && !store.isBusy) rightTab.value = 'import';
watch(() => store.entries.length, (n) => {
  if (n > 0 && !hadEntries.value && rightTab.value === 'import') rightTab.value = 'entries';
  if (n === 0 && hadEntries.value) rightTab.value = 'import';
  hadEntries.value = n > 0;
});
</script>

<template>
  <div
    :class="isSidebar ? '' : 'bg-ground b-0 b-t b-solid b-content'"
    class="h-full flex items-stretch of-hidden"
  >
    <div :class="isSidebar ? 'w-12' : isMobile ? 'w-full' : 'w-lg'" class="h-full flex flex-col">
      <!-- collapsed rail (WorkspaceInfo.vue:29-69) -->
      <div v-if="isSidebar" class="grow-1 flex flex-col items-center" data-test="panel-rail">
        <Button v-tooltip.left="'Show panel'" icon="i-tabler-chevrons-left" class="w-full h-12 rd-0" severity="secondary" text @click="emit('update:collapsed', false)" />
        <Button v-tooltip.left="'Open in Nanome'" class="w-full h-12 rd-0" severity="secondary" text @click="emit('open-in-nanome')"><template #icon="iconProps"><NanomeIcon :class="iconProps.class" /></template></Button>
        <Button v-tooltip.left="'Undo'" :disabled="!store.canUndo" class="w-full h-12 rd-0" icon="i-tabler-arrow-back-up" severity="secondary" text @click="store.undo()" />
        <Button v-tooltip.left="'Redo'" :disabled="!store.canRedo" class="w-full h-12 rd-0" icon="i-tabler-arrow-forward-up" severity="secondary" text @click="store.redo()" />
      </div>

      <template v-else>
        <!-- header row (WorkspaceInfo.vue:72-108) -->
        <div class="min-h-0 p-4 pb-0 flex items-center gap-2">
          <Button
            v-tooltip.bottom="isMobile ? '' : 'Hide panel'"
            :icon="isMobile ? 'i-tabler-x' : 'i-tabler-chevrons-right'"
            class="shrink-0"
            severity="secondary"
            rounded
            text
            data-test="panel-collapse"
            @click="emit('update:collapsed', true)"
          />
          <div class="mr-auto truncate text-xl font-medium">
            {{ title }}
          </div>
          <Button
            v-tooltip.bottom="'Open in Nanome'"
            class="shrink-0"
            severity="secondary"
            rounded
            text
            @click="emit('open-in-nanome')"><template #icon="iconProps"><NanomeIcon :class="iconProps.class" /></template></Button>
        </div>

        <Tabs v-model:value="rightTab" class="w-full min-h-0 flex-1 flex flex-col" data-test="panel-tabs">
          <TabList>
            <Tab value="entries">
              Entries
            </Tab>
            <Tab value="ligands">
              Ligands
            </Tab>
            <Tab value="mara">
              MARA
            </Tab>
            <Tab value="import">
              Import
            </Tab>
            <Tab value="users">
              Users
            </Tab>
          </TabList>
          <TabPanels class="min-h-0 flex-1 flex flex-col py-0">
            <TabPanel class="min-h-0 flex-1 flex flex-col p-4 of-y-auto" value="entries">
              <WorkspaceEntries>
                <template #actions>
                  <Button v-tooltip.bottom="'Undo'" :disabled="!store.canUndo" icon="i-tabler-arrow-back-up" severity="secondary" size="small" rounded data-test="entries-undo" @click="store.undo()" />
                  <Button v-tooltip.bottom="'Redo'" :disabled="!store.canRedo" icon="i-tabler-arrow-forward-up" severity="secondary" size="small" rounded data-test="entries-redo" @click="store.redo()" />
                </template>
              </WorkspaceEntries>
            </TabPanel>
            <TabPanel class="min-h-0 flex-1 flex flex-col gap-4 p-4" value="ligands">
              <WorkspaceLigands />
            </TabPanel>
            <TabPanel class="min-h-0 flex-1 flex flex-col" value="mara">
              <WorkspaceChat />
            </TabPanel>
            <TabPanel class="min-h-0 flex-1 flex flex-col p-4" value="import">
              <WorkspaceImportFile v-if="rightTab === 'import'" @imported="rightTab = 'entries'" />
            </TabPanel>
            <TabPanel class="min-h-0 flex-1 flex flex-col p-4" value="users">
              <WorkspaceMembers />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </template>
    </div>
  </div>
</template>
